import {
  TrainingRequirement,
  TrainingCourse,
  TrainingEvent,
  TrainingAssignment,
  TrainingRecord,
  PersonQualification,
  CurrencyRecord,
  TrainingEffectivenessAssessment,
  TrainingDashboardKpis,
} from './types';

const BASE_URL = '/api/training';
const DEFAULT_ORG_ID = 'ORG-XYZ';

function getHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'x-organization-id': DEFAULT_ORG_ID,
  };
}

export const trainingService = {
  // KPI Dashboard
  async getDashboardKpis(): Promise<TrainingDashboardKpis> {
    const res = await fetch(`${BASE_URL}/analytics/dashboard`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  // Requirements (The "WHY")
  async getRequirements(filters?: { source_type?: string; status?: string }): Promise<TrainingRequirement[]> {
    const params = new URLSearchParams();
    if (filters?.source_type) params.append('source_type', filters.source_type);
    if (filters?.status) params.append('status', filters.status);

    const res = await fetch(`${BASE_URL}/requirements?${params.toString()}`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  async createRequirement(data: any): Promise<TrainingRequirement> {
    const res = await fetch(`${BASE_URL}/requirements`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to create requirement');
    return json.data;
  },

  // Courses (The "WHAT")
  async getCourses(filters?: { category?: string; status?: string; search?: string }): Promise<TrainingCourse[]> {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);

    const res = await fetch(`${BASE_URL}/courses?${params.toString()}`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  async createCourse(data: any): Promise<TrainingCourse> {
    const res = await fetch(`${BASE_URL}/courses`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to create course');
    return json.data;
  },

  // Events / Sessions
  async getEvents(filters?: { course_id?: string; status?: string }): Promise<TrainingEvent[]> {
    const params = new URLSearchParams();
    if (filters?.course_id) params.append('course_id', filters.course_id);
    if (filters?.status) params.append('status', filters.status);

    const res = await fetch(`${BASE_URL}/events?${params.toString()}`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  async createEvent(data: any): Promise<TrainingEvent> {
    const res = await fetch(`${BASE_URL}/events`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to create event');
    return json.data;
  },

  // Assignments (The "WHO")
  async getAssignments(filters?: { person_id?: string; course_id?: string; status?: string }): Promise<TrainingAssignment[]> {
    const params = new URLSearchParams();
    if (filters?.person_id) params.append('person_id', filters.person_id);
    if (filters?.course_id) params.append('course_id', filters.course_id);
    if (filters?.status) params.append('status', filters.status);

    const res = await fetch(`${BASE_URL}/assignments?${params.toString()}`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  async createAssignment(data: any): Promise<TrainingAssignment> {
    const res = await fetch(`${BASE_URL}/assignments`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to create assignment');
    return json.data;
  },

  // Records (The "WHAT HAPPENED")
  async getRecords(filters?: { person_id?: string; course_id?: string; result?: string }): Promise<TrainingRecord[]> {
    const params = new URLSearchParams();
    if (filters?.person_id) params.append('person_id', filters.person_id);
    if (filters?.course_id) params.append('course_id', filters.course_id);
    if (filters?.result) params.append('result', filters.result);

    const res = await fetch(`${BASE_URL}/records?${params.toString()}`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  async recordCompletion(data: any): Promise<TrainingRecord> {
    const res = await fetch(`${BASE_URL}/records`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to record completion');
    return json.data;
  },

  // Qualifications & Currency
  async getQualifications(): Promise<PersonQualification[]> {
    const res = await fetch(`${BASE_URL}/qualifications`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  async getCurrencies(): Promise<CurrencyRecord[]> {
    const res = await fetch(`${BASE_URL}/currencies`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  // Effectiveness
  async getEffectivenessAssessments(): Promise<TrainingEffectivenessAssessment[]> {
    const res = await fetch(`${BASE_URL}/effectiveness`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  async recordEffectiveness(data: any): Promise<TrainingEffectivenessAssessment> {
    const res = await fetch(`${BASE_URL}/effectiveness`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to record effectiveness');
    return json.data;
  },
};
